import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase, supabaseConfigured } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { usePhoenixRefresh } from './usePhoenixRefresh'
import { useQueueByTable } from './useQueueByTable'
import { enqueueMutation } from '../lib/mutationQueue'
import { getCached, setCached } from '../lib/hookCache'

const TABLE = 'meal_entries'
const CACHE_SCOPE = 'meal_entries'

// Mirrors useWaterEntries: mutation-queue-driven writes with optimistic
// merge, module-level cache for instant tab-switch display, and
// initial-load-only loading flag.
export function useMealEntries() {
  const { user } = useAuth()
  const cached = getCached(CACHE_SCOPE, user?.id)
  const [serverEntries, setServerEntries] = useState(cached || [])
  const [loading, setLoading] = useState(!cached)
  const [error, setError] = useState(null)
  const pending = useQueueByTable(TABLE)
  const initialLoadedRef = useRef(!!cached)

  const fetchEntries = useCallback(async () => {
    if (!supabaseConfigured || !user) {
      setLoading(false)
      return
    }
    if (!initialLoadedRef.current) setLoading(true)
    setError(null)
    const { data, error: err } = await supabase
      .from(TABLE)
      .select('*')
      .order('date', { ascending: false })
      .order('created_at', { ascending: false })
    if (err) {
      setError(err.message)
    } else {
      const next = data || []
      setServerEntries(next)
      setCached(CACHE_SCOPE, user.id, next)
    }
    initialLoadedRef.current = true
    setLoading(false)
  }, [user])

  useEffect(() => { fetchEntries() }, [fetchEntries])
  usePhoenixRefresh(fetchEntries)

  // Optimistic merge of queued mutations onto server snapshot.
  const entries = useMemo(() => {
    let view = [...serverEntries]
    for (const m of pending) {
      if (m.op === 'upsert') {
        // Inserts get a temporary id from the mutation queue; updates
        // target an existing row by id.
        const idx = view.findIndex((e) => e.id === m.payload.id)
        if (idx >= 0) view[idx] = { ...view[idx], ...m.payload, _pending: m }
        else view.unshift({ ...m.payload, _pending: m })
      } else if (m.op === 'update') {
        const idx = view.findIndex((e) => e.id === m.payload.id)
        if (idx >= 0) view[idx] = { ...view[idx], ...m.payload, _pending: m }
      } else if (m.op === 'delete') {
        view = view.filter((e) => e.id !== m.payload.id)
      }
    }
    return view
  }, [serverEntries, pending])

  // Strip empty strings and clamp numerics. The DB has CHECK (kcal >= 0)
  // so we guard here to keep the optimistic UI honest.
  const sanitize = (values) => {
    const out = { name: values.name?.trim() || '', kcal: Math.max(0, Math.round(Number(values.kcal) || 0)) }
    out.meal_type = values.meal_type
    out.protein_g = values.protein_g === '' || values.protein_g == null ? null : Number(values.protein_g)
    out.carbs_g = values.carbs_g === '' || values.carbs_g == null ? null : Number(values.carbs_g)
    out.fat_g = values.fat_g === '' || values.fat_g == null ? null : Number(values.fat_g)
    out.notes = values.notes?.trim() || null
    return out
  }

  const addMeal = async (values) => {
    if (!user) return false
    setError(null)
    const id = crypto.randomUUID()
    await enqueueMutation({
      id,
      table: TABLE,
      op: 'upsert',
      payload: {
        id,
        user_id: user.id,
        date: values.date,
        ...sanitize(values),
        updated_at: new Date().toISOString(),
      },
      conflictKey: 'id',
      userId: user.id,
    })
    return true
  }

  const updateMeal = async (id, values) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      id,
      table: TABLE,
      op: 'update',
      payload: {
        id,
        ...sanitize(values),
        ...(values.date ? { date: values.date } : {}),
        updated_at: new Date().toISOString(),
      },
      userId: user.id,
    })
    return true
  }

  const deleteMeal = async (id) => {
    if (!user) return false
    setError(null)
    await enqueueMutation({
      id,
      table: TABLE,
      op: 'delete',
      payload: { id },
      userId: user.id,
    })
    return true
  }

  return {
    entries,
    loading,
    error,
    addMeal,
    updateMeal,
    deleteMeal,
    refetch: fetchEntries,
  }
}
